import { useState, useEffect, useMemo } from 'react';
import { X, Eye, Trash2, CheckCircle, Clock, Loader2, RefreshCw, Upload, FileText, Edit, CheckCheck } from 'lucide-react';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { corporateCarrierService, type CorporateCarrier } from '../services/corporateCarrierService';
import { toast } from 'sonner';
import { FlexibleTracking } from './FlexibleTracking';
import { useAuth } from '../contexts/AuthContext';

const APPLICATION_DOCUMENT_LABELS: Record<string, string> = {
  'tax-plate': 'Vergi Levhasi',
  'license': 'Ehliyet',
  'tractor-license': 'Cekici Ruhsat',
  'trailer-license': 'Dorse Ruhsat',
  'src': 'SRC Belgesi',
  'vehicle-insurance': 'Arac Sigortasi',
  'contract': 'Tasima Sozlesmesi',
  'carrier-authority': 'Tasiyici Yetki Belgesi',
  'psychotechnic': 'Psikoteknik Belgesi',
};

const APPLICATION_DOCUMENT_TYPES = Object.keys(APPLICATION_DOCUMENT_LABELS);

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://') || pathStr.startsWith('data:')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

const normalizeSearchText = (value?: string | null) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();
const normalizePhone = (value?: string | null) => String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
const normalizePlateText = (value?: string | null) => normalizeSearchText(value).replace(/\s+/g, '');
const normalizeUpper = (value?: string | null) => String(value || '').trim().toLocaleUpperCase('tr-TR');
const normalizePlate = (value?: string | null) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
const isValidPlate = (value?: string | null) => {
  const plate = normalizePlate(value);

  return (
    !plate ||
    (plate.length >= 6 &&
      plate.length <= 9 &&
      /^\d{2}[A-ZÇĞİÖŞÜ]{1,3}\d{2,4}$/.test(plate))
  );
};

  interface DriverApplication {
  id: number;
  workiomRecordId?: string;
  source?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  name?: string;
  phone: string;
  email: string;
  tcNo?: string;
  birthDate?: string;
  city: string;
  district?: string;
  address?: string;
  licenseType: string;
  licenseDate?: string;
  licenseNumber?: string;
  srcBelgeNo?: string;
  srcBelgeDate?: string;
  srcExpiryDate?: string;
  invoiceTitle?: string;
  taxOffice?: string;
  taxNumber?: string;
  startDate?: string;
  terminationDate?: string;
  isControlResponsible?: boolean;
  harcirahTuru?: string;
  workDuration?: string;
  currentWeekHours?: string;
  twoWeekHours?: string;
  residenceLatitude?: string;
  residenceLongitude?: string;
  hasVehicle?: 'yes' | 'no';
  vehiclePlate?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  vehicleType?: string;
  vehicleCapacity?: string;
  trailerPlate?: string;
  trailerType?: string;
  experience: string;
  previousCompanies?: string;
  notes?: string;
  appliedAt?: string;
  status: 'pending' | 'approved' | 'rejected' | 'interview';
  externalTransportAccountUserId?: string;
  externalTransportAccountName?: string;
  searchIndex?: string;
}

export function DriverApplications() {
  const { user } = useAuth();
  const [data, setData] = useState<DriverApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<DriverApplication | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingApplication, setEditingApplication] = useState<DriverApplication | null>(null);
  const [previewDocument, setPreviewDocument] = useState<{ name: string; url: string; isImage: boolean } | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [carrierCompanies, setCarrierCompanies] = useState<CorporateCarrier[]>([]);

  useEffect(() => {
    loadApplications();
    loadCarrierCompanies();
  }, []);

  const loadCarrierCompanies = async () => {
    try {
      const response: any = await corporateCarrierService.getAll({ status: 'active' });
      setCarrierCompanies(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load corporate carriers:', error);
      setCarrierCompanies([]);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await driverService.getApplications({ all: true });
      
      if (response.success && response.data) {
        const mappedData: DriverApplication[] = response.data.map((app: any) => ({
          id: app.id,
          workiomRecordId: app.workiom_record_id || '',
          source: app.source || 'web',
          firstName: app.first_name || app.firstName,
          lastName: app.last_name || app.lastName,
          fullName: app.full_name || app.fullName || `${app.first_name || ''} ${app.last_name || ''}`.trim(),
          name: app.full_name || app.name,
          phone: app.phone || '',
          email: app.email || '',
          tcNo: app.tc_number || app.tcNo,
          birthDate: app.date_of_birth || app.birthDate,
          city: app.city || '',
          district: app.district || '',
          address: app.address || '',
          licenseType: app.license_class || app.licenseType || '',
          licenseDate: app.license_date || app.licenseDate,
          licenseNumber: app.license_number || app.licenseNumber,
          srcBelgeNo: app.src_card_number || app.srcBelgeNo,
          srcBelgeDate: app.src_card_expiry || app.srcBelgeDate,
          srcExpiryDate: app.src_expiry_date || app.srcExpiryDate,
          invoiceTitle: app.invoice_title || app.invoiceTitle,
          taxOffice: app.tax_office || app.taxOffice,
          taxNumber: app.tax_number || app.taxNumber,
          startDate: app.start_date || app.startDate,
          terminationDate: app.termination_date || app.terminationDate,
          isControlResponsible: Boolean(app.is_control_responsible),
          harcirahTuru: app.harcirah_turu || 'sabit',
          workDuration: app.work_duration || app.workDuration,
          currentWeekHours: app.current_week_hours || app.currentWeekHours,
          twoWeekHours: app.two_week_hours || app.twoWeekHours,
          residenceLatitude: app.residence_latitude ?? app.residenceLatitude ?? '',
          residenceLongitude: app.residence_longitude ?? app.residenceLongitude ?? '',
          hasVehicle: app.has_vehicle === 1 || app.has_vehicle === 'yes' ? 'yes' : 'no',
          vehiclePlate: app.cekici_plaka || app.vehiclePlate,
          vehicleBrand: app.vehicle_brand || app.vehicleBrand,
          vehicleModel: app.vehicle_model || app.vehicleModel,
          vehicleYear: app.vehicle_year || app.vehicleYear,
          vehicleType: app.vehicle_type || app.vehicleType,
          vehicleCapacity: app.vehicle_capacity || app.vehicleCapacity,
          trailerPlate: app.dorse_plaka || app.trailerPlate,
          trailerType: app.dorse_type || app.trailerType,
          experience: app.experience_years?.toString() || app.experience?.toString() || '0',
          previousCompanies: app.previous_companies || app.previousCompanies,
          notes: app.notes || '',
          appliedAt: app.created_at || app.appliedAt,
          status: mapApplicationStatus(app.status),
          externalTransportAccountUserId: app.external_transport_account_user_id ? String(app.external_transport_account_user_id) : '',
          externalTransportAccountName: app.external_transport_company_title || app.external_transport_account_name || '',
          searchIndex: [
            app.full_name || app.fullName || `${app.first_name || ''} ${app.last_name || ''}`.trim(),
            app.phone,
            app.email,
            app.tc_number || app.tcNo,
            app.city,
            app.district,
            app.address,
            app.external_transport_company_title || app.external_transport_account_name,
            app.cekici_plaka || app.vehiclePlate,
            app.dorse_plaka || app.trailerPlate,
            app.vehicle_type || app.vehicleType,
            app.license_class || app.licenseType,
            app.license_number,
            app.src_card_number,
            normalizePhone(app.phone),
            normalizePhone(app.tc_number || app.tcNo),
            normalizePlateText(app.cekici_plaka || app.vehiclePlate),
            normalizePlateText(app.dorse_plaka || app.trailerPlate),
          ].map((item) => normalizeSearchText(String(item || ''))).join(' '),
        }));
        setData(mappedData);
      } else {
        toast.error(response.message || 'Başvurular yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Başvurular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const mapApplicationStatus = (status: string): DriverApplication['status'] => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'onaylandi':
        return 'approved';
      case 'rejected':
      case 'reddedildi':
        return 'rejected';
      case 'interview':
      case 'gorusme':
        return 'interview';
      default:
        return 'pending';
    }
  };

  const updateStatus = async (id: number, newStatus: DriverApplication['status']) => {
    try {
      setUpdating(id.toString());
      
      const response = await driverService.updateApplicationStatus(id, newStatus);
      
      if (response.success) {
        toast.success(`Başvuru durumu güncellendi: ${getStatusLabel(newStatus)}`);
        
        if (newStatus === 'approved') {
          const application = data.find(app => app.id === id);
          if (application) {
            toast.success(`${application.fullName || application.name || 'Sürücü'} onaylandı ve onaylı sürücüler listesine eklendi`);
          }
        }
        
        setData(data.map(app => 
          app.id === id ? { ...app, status: newStatus } : app
        ));
        setSelectedApplication(null);
        await loadApplications();
      } else {
        toast.error(response.message || 'Güncelleme başarısız');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Güncelleme hatası');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      case 'interview': return 'Görüşme';
      default: return status;
    }
  };

  const deleteApplication = async (id: number) => {
    if (!confirm('Bu başvuruyu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await driverService.deleteApplication?.(id);
      
      if (response?.success) {
        toast.success('Başvuru silindi');
        setData(data.filter(app => app.id !== id));
      } else {
        toast.error(response?.message || 'Silme başarısız');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Silme hatası');
    }
  };

  const getStatusBadge = (status: DriverApplication['status']) => {
    const statusMap = {
      'pending': { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'approved': { label: 'Onaylandı', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'rejected': { label: 'Reddedildi', color: 'bg-red-100 text-red-800', icon: XCircle },
      'interview': { label: 'Görüşme', color: 'bg-blue-100 text-blue-800', icon: Eye },
    };
    const config = statusMap[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getDisplayName = (app: DriverApplication) => {
    return app.fullName || app.name || `${app.firstName || ''} ${app.lastName || ''}`.trim() || app.email;
  };

  const operationRows: any[] = useMemo(() => data.map((app) => ({
    rowKey: String(app.id),
    id: String(app.id),
    sevkKodu: '',
    planKodu: '',
    cekici: app.vehiclePlate || '-',
    dorse: app.trailerPlate || '-',
    surucuAdi: getDisplayName(app),
    irtibatTelefon: app.phone || '-',
    surucuTelefon: app.phone || '-',
    durum: app.status,
    yuklemeIli: app.city || '',
    teslimIlcesi: app.district || '',
    spotSource: app.source === 'mobile-app' ? 'Mobil' : (app.source === 'web' ? 'Web' : ''),
    spotEmail: app.email || '',
    spotEhliyetSinifi: app.licenseType || '',
    spotSrcNo: app.srcBelgeNo || '',
    spotDeneyimYil: app.experience || '',
    spotSehirIlce: app.district ? `${app.city || ''} / ${app.district}` : (app.city || ''),
    spotKurumsalFirma: app.externalTransportAccountName || '',
    spotKontrolSorumlusu: app.isControlResponsible ? 'Evet' : '',
    spotIkametKonum: (app.residenceLatitude && app.residenceLongitude) ? `${app.residenceLatitude}, ${app.residenceLongitude}` : '',
    createdAt: app.appliedAt || '',
    updatedAt: app.appliedAt || '',
    _rawApp: app,
  })), [data]);

  const statusColumnRender = (row: any) => getStatusBadge(mapApplicationStatus(String(row.durum)));

  const externalRowActions = (row: any) => {
    const app: DriverApplication = row._rawApp;
    const status = mapApplicationStatus(app.status);
    return (
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setSelectedApplication(app)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"><Eye className="h-3 w-3 shrink-0" />Detay</button>
        {(status === 'pending' || status === 'interview') && <button type="button" onClick={() => void updateStatus(app.id, 'approved')} disabled={updating === String(app.id)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"><CheckCircle className="h-3 w-3 shrink-0" />Onayla</button>}
        {(status === 'pending' || status === 'interview') && <button type="button" onClick={() => void updateStatus(app.id, 'rejected')} disabled={updating === String(app.id)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"><XCircle className="h-3 w-3 shrink-0" />Reddet</button>}
        <button type="button" onClick={() => { setEditingApplication(app); setIsCreateModalOpen(true); }} className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"><Edit className="h-3 w-3 shrink-0" />Düzenle</button>
        <button type="button" onClick={() => deleteApplication(app.id)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100"><Trash2 className="h-3 w-3 shrink-0" />Sil</button>
      </div>
    );
  };

  const approveAll = async () => {
    const count = data.filter((a) => a.status === 'pending' || a.status === 'interview').length;
    if (count === 0) {
      toast.info('Onay bekleyen başvuru yok');
      return;
    }
    if (!confirm(`${count} bekleyen başvuru onaylanacak. Devam edilsin mi?`)) return;
    try {
      setApprovingAll(true);
      const res: any = await driverService.approveAllApplications(user?.id);
      if (res?.success) {
        toast.success(res?.message || 'Tüm bekleyen başvurular onaylandı');
        await loadApplications();
      } else {
        toast.error(res?.message || 'Toplu onaylama başarısız');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Toplu onaylama başarısız');
    } finally {
      setApprovingAll(false);
    }
  };

  const pendingCount = data.filter((a) => a.status === 'pending' || a.status === 'interview').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Spot Sürücü Başvuruları</h1>
          <p className="text-gray-500 mt-1">Dış kaynak sürücü başvurularını yönetin - Toplam {data.length} başvuru</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void approveAll()}
            disabled={approvingAll}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
            title="Tüm bekleyen başvuruları onayla"
          >
            {approvingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
            Tümünü Onayla ({pendingCount})
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingApplication(null);
              setIsCreateModalOpen(true);
            }}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Yeni Spot Sürücü
          </button>
          <button
            onClick={loadApplications}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-5 h-5" />
            Yenile
          </button>
        </div>

      </div>

      <FlexibleTracking
        externalRows={operationRows as any}
        externalLoading={loading}
        onExternalRefresh={loadApplications}
        dataService={{} as any}
        settingsKeyScope="driver_applications"
        title="Spot Sürücü Başvuruları"
        subtitle="Dış kaynak sürücü başvurularını yönetin"
        hideArchiveTab
        hideCreateActions
        disableDetailPanel
        externalRowActions={externalRowActions}
        defaultVisibleColumns={['surucuAdi', 'spotKurumsalFirma', 'surucuTelefon', 'cekici', 'dorse', 'spotEhliyetSinifi', 'spotSrcNo', 'spotSehirIlce', 'durum', 'spotSource', 'createdAt'] as any}
        flowStatusOptions={[
          { value: 'pending', label: 'Beklemede' },
          { value: 'interview', label: 'Görüşme' },
          { value: 'approved', label: 'Onaylandı' },
          { value: 'rejected', label: 'Reddedildi' },
        ] as any}
      />

      {/* Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {getDisplayName(selectedApplication)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Başvuru Detayları</p>
              </div>
              <button onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Kişisel Bilgiler */}
              <div className="bg-blue-50 rounded-lg p-5 border-2 border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Kişisel Bilgiler</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">TC Kimlik No</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.tcNo || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Doğum Tarihi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedApplication.birthDate ? new Date(selectedApplication.birthDate).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Telefon</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">E-posta</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Kurumsal Nakliye Firması</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.externalTransportAccountName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">İl / İlçe</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.city} / {selectedApplication.district || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Adres</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.address || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Ehliyet Bilgileri */}
              <div className="bg-green-50 rounded-lg p-5 border-2 border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Ehliyet ve Belgeler</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-white p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Ehliyet Sınıfı</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.licenseType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">SRC Belge No</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.srcBelgeNo || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Araç Bilgileri */}
              {selectedApplication.hasVehicle === 'yes' && (
                <div className="bg-purple-50 rounded-lg p-5 border-2 border-purple-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-purple-600" />
                      Araç Bilgileri
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-white p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Plaka</label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold">{selectedApplication.vehiclePlate}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Araç Tipi</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedApplication.vehicleType}</p>
                    </div>
                    {selectedApplication.trailerPlate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Dorse Plaka</label>
                        <p className="mt-1 text-sm text-gray-900 font-semibold">{selectedApplication.trailerPlate}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Deneyim */}
              <div className="bg-orange-50 rounded-lg p-5 border-2 border-orange-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{selectedApplication.hasVehicle === 'yes' ? '4' : '3'}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Deneyim Bilgileri</h3>
                </div>
                <div className="space-y-4 bg-white p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Toplam Deneyim</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.experience} yıl</p>
                  </div>
                  {selectedApplication.previousCompanies && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Önceki Firmalar</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedApplication.previousCompanies}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Durum Değiştirme */}
              <div className="bg-gray-50 rounded-lg p-5 border-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Başvuru Durumu</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => updateStatus(selectedApplication.id, 'pending')}
                    disabled={updating === selectedApplication.id.toString()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedApplication.status === 'pending'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    } ${updating === selectedApplication.id.toString() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Clock className="w-4 h-4 inline mr-2" />
                    Beklemede
                  </button>
                  <button
                    onClick={() => updateStatus(selectedApplication.id, 'interview')}
                    disabled={updating === selectedApplication.id.toString()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedApplication.status === 'interview'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    } ${updating === selectedApplication.id.toString() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Eye className="w-4 h-4 inline mr-2" />
                    Görüşmeye Çağır
                  </button>
                  <button
                    onClick={() => updateStatus(selectedApplication.id, 'approved')}
                    disabled={updating === selectedApplication.id.toString()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedApplication.status === 'approved'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    } ${updating === selectedApplication.id.toString() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                    Onayla
                  </button>
                  <button
                    onClick={() => updateStatus(selectedApplication.id, 'rejected')}
                    disabled={updating === selectedApplication.id.toString()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedApplication.status === 'rejected'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    } ${updating === selectedApplication.id.toString() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <XCircle className="w-4 h-4 inline mr-2" />
                    Reddet
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedApplication(null)}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <CreateSpotDriverModal
          application={editingApplication}
          carrierCompanies={carrierCompanies}
          onClose={() => {
            setEditingApplication(null);
            setIsCreateModalOpen(false);
          }}
          onSaved={async () => {
            await loadApplications();
            setEditingApplication(null);
            setIsCreateModalOpen(false);
          }}
        />
      )}

      {previewDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreviewDocument(null)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div className="truncate text-sm font-semibold text-gray-900">{previewDocument.name}</div>
              <button type="button" onClick={() => setPreviewDocument(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex max-h-[78vh] items-center justify-center overflow-auto bg-gray-100 p-4">
              {previewDocument.isImage ? <img src={previewDocument.url} alt={previewDocument.name} className="max-h-[72vh] max-w-full rounded-lg object-contain" /> : <embed src={previewDocument.url} title={previewDocument.name} type="application/pdf" className="h-[72vh] w-full rounded-lg bg-white" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateSpotDriverModal({ application, carrierCompanies, onClose, onSaved }: { application: DriverApplication | null; carrierCompanies: CorporateCarrier[]; onClose: () => void; onSaved: () => Promise<void> | void }) {
  const isEditMode = Boolean(application?.id);
  const [saving, setSaving] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<Record<string, File | null>>({});
  const [formData, setFormData] = useState(() => ({
    full_name: application?.fullName || application?.name || '',
    phone: application?.phone || '',
    email: application?.email || '',
    tc_number: application?.tcNo || '',
    city: application?.city || '',
    district: application?.district || '',
    address: application?.address || '',
    license_number: application?.licenseNumber || '',
    license_class: application?.licenseType || 'E',
    experience_years: Number(application?.experience || 0),
    cekici_plaka: application?.vehiclePlate || '',
    dorse_plaka: application?.trailerPlate || '',
    vehicle_type: application?.vehicleType || '',
    invoice_title: application?.invoiceTitle || '',
    tax_office: application?.taxOffice || '',
    tax_number: application?.taxNumber || '',
    external_transport_account_user_id: application?.externalTransportAccountUserId || '',
    external_transport_account_name: application?.externalTransportAccountName || '',
    src_card_number: application?.srcBelgeNo || '',
    src_expiry_date: application?.srcExpiryDate || application?.srcBelgeDate || '',
    start_date: application?.startDate || '',
    termination_date: application?.terminationDate || '',
    is_control_responsible: Boolean(application?.isControlResponsible),
    harcirah_turu: application?.harcirahTuru || 'sabit',
    work_duration: application?.workDuration || '',
    current_week_hours: application?.currentWeekHours || '',
    two_week_hours: application?.twoWeekHours || '',
    residence_latitude: application?.residenceLatitude ?? '',
    residence_longitude: application?.residenceLongitude ?? '',
  }));

  useEffect(() => {
    setFormData({
      full_name: application?.fullName || application?.name || '',
      phone: application?.phone || '',
      email: application?.email || '',
      tc_number: application?.tcNo || '',
      city: application?.city || '',
      district: application?.district || '',
      address: application?.address || '',
      license_number: '',
      license_class: application?.licenseType || 'E',
      experience_years: Number(application?.experience || 0),
      cekici_plaka: application?.vehiclePlate || '',
      dorse_plaka: application?.trailerPlate || '',
      vehicle_type: application?.vehicleType || '',
      invoice_title: '',
      tax_office: '',
      tax_number: '',
      external_transport_account_user_id: application?.externalTransportAccountUserId || '',
      external_transport_account_name: application?.externalTransportAccountName || '',
      src_card_number: '',
      src_expiry_date: '',
      start_date: '',
      termination_date: '',
      is_control_responsible: false,
      harcirah_turu: 'sabit',
      work_duration: '',
      current_week_hours: '',
      two_week_hours: '',
      residence_latitude: '',
      residence_longitude: '',
    });
    setPendingDocuments({});
  }, [application]);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!application?.id) {
        setExistingDocuments([]);
        return;
      }
      try {
        const response: any = await documentService.getDocuments({ entityType: 'driver_application', entityId: String(application.id) });
        setExistingDocuments(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Application documents load failed:', error);
        setExistingDocuments([]);
      }
    };
    void loadDocuments();
  }, [application?.id]);

  const uploadPendingDocuments = async (applicationId: string | number) => {
    const uploads = Object.entries(pendingDocuments).filter(([, file]) => file instanceof File) as Array<[string, File]>;
    for (const [documentType, file] of uploads) {
      const form = new FormData();
      form.append('file', file);
      form.append('entityType', 'driver_application');
      form.append('entityId', String(applicationId));
      form.append('documentType', documentType);
      form.append('name', file.name);
      await documentService.uploadDocument(form);
    }
  };

  const handleSave = async () => {
    if (!formData.full_name.trim() || !formData.phone.trim()) {
      toast.error('Ad soyad ve telefon zorunludur');
      return;
    }
    if (!isValidPlate(formData.cekici_plaka)) return toast.error('Çekici plaka formatı geçersiz');
    if (!isValidPlate(formData.dorse_plaka)) return toast.error('Dorse plaka formatı geçersiz');

    const payload = {
      ...formData,
      full_name: normalizeUpper(formData.full_name),
      phone: normalizePhone(formData.phone),
      email: formData.email.trim(),
      tc_number: formData.tc_number.trim(),
      city: normalizeUpper(formData.city),
      district: normalizeUpper(formData.district),
      address: formData.address.trim(),
      license_number: formData.license_number.trim(),
      license_class: normalizeUpper(formData.license_class),
      cekici_plaka: normalizePlate(formData.cekici_plaka),
      dorse_plaka: normalizePlate(formData.dorse_plaka),
      vehicle_type: normalizeUpper(formData.vehicle_type),
      invoice_title: normalizeUpper(formData.invoice_title),
      tax_office: normalizeUpper(formData.tax_office),
      tax_number: formData.tax_number.trim(),
      external_transport_account_user_id: formData.external_transport_account_user_id || null,
      src_card_number: formData.src_card_number || null,
      src_expiry_date: formData.src_expiry_date || null,
      start_date: formData.start_date || null,
      termination_date: formData.termination_date || null,
      is_control_responsible: formData.is_control_responsible ? 1 : 0,
      harcirah_turu: formData.harcirah_turu || 'sabit',
      work_duration: formData.work_duration || null,
      current_week_hours: formData.current_week_hours || null,
      two_week_hours: formData.two_week_hours || null,
      residence_latitude: formData.residence_latitude || null,
      residence_longitude: formData.residence_longitude || null,
    };

    try {
      setSaving(true);
      let applicationId = application?.id;
      if (isEditMode && applicationId) {
        await driverService.updateApplication(applicationId, payload);
      } else {
        const response: any = await driverService.createApplication(payload);
        applicationId = response?.data?.id || response?.id || response?.data?.applicationId;
      }
      if (applicationId) {
        await uploadPendingDocuments(applicationId);
      }
      toast.success(isEditMode ? 'Spot sürücü kaydı güncellendi' : 'Spot sürücü kaydı oluşturuldu');
      await onSaved();
    } catch (error: any) {
      console.error('Save spot driver failed:', error);
      toast.error(error?.message || (isEditMode ? 'Spot sürücü güncellenemedi' : 'Spot sürücü oluşturulamadı'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId: string | number) => {
    try {
      await documentService.deleteDocument(documentId);
      setExistingDocuments((current) => current.filter((item) => String(item.id) !== String(documentId)));
      toast.success('Evrak silindi');
    } catch (error: any) {
      console.error('Delete application document failed:', error);
      toast.error(error?.message || 'Evrak silinemedi');
    }
  };

  const inputClassName = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm';
  const carrierListId = `driver-application-carriers-${application?.id || 'new'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Spot Surucu Duzenle' : 'Yeni Spot Surucu'}</h2>
            <p className="mt-1 text-sm text-gray-500">Basvuru ekranindan manuel spot surucu kaydi ve evrak yonetimi</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Kisisel Bilgiler</h3>
            <input value={formData.full_name} onChange={(e) => setFormData((prev) => ({ ...prev, full_name: normalizeUpper(e.target.value) }))} placeholder="Ad Soyad" className={inputClassName} />
            <input value={formData.phone} onChange={(e) => setFormData((prev) => ({ ...prev, phone: normalizePhone(e.target.value) }))} placeholder="Telefon (5XXXXXXXXX)" className={inputClassName} />
            <input value={formData.email} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} placeholder="E-posta" className={inputClassName} />
            <div>
              <input
                value={formData.external_transport_account_name}
                list={carrierListId}
                onChange={(event) => {
                  const value = event.target.value;
                  const carrier = carrierCompanies.find((item) => item.companyTitle === value || item.fullName === value);
                  setFormData((prev) => ({
                    ...prev,
                    external_transport_account_name: value,
                    external_transport_account_user_id: carrier ? carrier.id : '',
                  }));
                }}
                placeholder="Kurumsal Nakliye Firması"
                className={inputClassName}
              />
              <datalist id={carrierListId}>
                {carrierCompanies.map((carrier) => <option key={carrier.id} value={carrier.companyTitle || carrier.fullName} />)}
              </datalist>
            </div>
            <input value={formData.tc_number} onChange={(e) => setFormData((prev) => ({ ...prev, tc_number: e.target.value }))} placeholder="TC Kimlik No" className={inputClassName} />
            <input value={formData.city} onChange={(e) => setFormData((prev) => ({ ...prev, city: normalizeUpper(e.target.value) }))} placeholder="Sehir" className={inputClassName} />
            <input value={formData.district} onChange={(e) => setFormData((prev) => ({ ...prev, district: normalizeUpper(e.target.value) }))} placeholder="Ilce" className={inputClassName} />
            <textarea value={formData.address} onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))} placeholder="Adres" rows={3} className={inputClassName} />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Arac ve Fatura Bilgileri</h3>
            <input value={formData.license_number} onChange={(e) => setFormData((prev) => ({ ...prev, license_number: e.target.value }))} placeholder="Ehliyet No" className={inputClassName} />
            <input value={formData.license_class} onChange={(e) => setFormData((prev) => ({ ...prev, license_class: normalizeUpper(e.target.value) }))} placeholder="Ehliyet Sinifi" className={inputClassName} />
            <input value={String(formData.experience_years)} onChange={(e) => setFormData((prev) => ({ ...prev, experience_years: Number(e.target.value || 0) }))} placeholder="Deneyim (Yil)" type="number" min="0" className={inputClassName} />
            <input value={formData.cekici_plaka} onChange={(e) => setFormData((prev) => ({ ...prev, cekici_plaka: normalizePlate(e.target.value) }))} placeholder="Cekici Plaka" className={inputClassName} />
            <input value={formData.dorse_plaka} onChange={(e) => setFormData((prev) => ({ ...prev, dorse_plaka: normalizePlate(e.target.value) }))} placeholder="Dorse Plaka" className={inputClassName} />
            <input value={formData.vehicle_type} onChange={(e) => setFormData((prev) => ({ ...prev, vehicle_type: normalizeUpper(e.target.value) }))} placeholder="Arac Tipi" className={inputClassName} />
            <input value={formData.invoice_title} onChange={(e) => setFormData((prev) => ({ ...prev, invoice_title: normalizeUpper(e.target.value) }))} placeholder="Fatura Unvani" className={inputClassName} />
            <div className="grid grid-cols-2 gap-3">
              <input value={formData.tax_office} onChange={(e) => setFormData((prev) => ({ ...prev, tax_office: normalizeUpper(e.target.value) }))} placeholder="Vergi Dairesi" className={inputClassName} />
              <input value={formData.tax_number} onChange={(e) => setFormData((prev) => ({ ...prev, tax_number: e.target.value }))} placeholder="Vergi No" className={inputClassName} />
            </div>
          </div>

          <div className="md:col-span-2 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">SRC ve Çalışma Bilgileri</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <input value={formData.src_card_number} onChange={(e) => setFormData((prev) => ({ ...prev, src_card_number: e.target.value }))} placeholder="SRC Kart Numarası" className={inputClassName} />
              <div><label className="block text-xs text-gray-500 mb-1">SRC Bitiş Tarihi</label><input type="date" value={formData.src_expiry_date} onChange={(e) => setFormData((prev) => ({ ...prev, src_expiry_date: e.target.value }))} className={inputClassName} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">İşe Başlama Tarihi</label><input type="date" value={formData.start_date} onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))} className={inputClassName} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">İşten Çıkış Tarihi</label><input type="date" value={formData.termination_date} onChange={(e) => setFormData((prev) => ({ ...prev, termination_date: e.target.value }))} className={inputClassName} /></div>
              <input value={formData.work_duration} onChange={(e) => setFormData((prev) => ({ ...prev, work_duration: e.target.value }))} placeholder="Çalışma Süresi" className={inputClassName} />
              <input value={formData.current_week_hours} onChange={(e) => setFormData((prev) => ({ ...prev, current_week_hours: e.target.value }))} placeholder="Bu Hafta Çalışma Saati" className={inputClassName} />
              <input value={formData.two_week_hours} onChange={(e) => setFormData((prev) => ({ ...prev, two_week_hours: e.target.value }))} placeholder="Son 2 Hafta Çalışma Saati" className={inputClassName} />
              <select value={formData.harcirah_turu} onChange={(e) => setFormData((prev) => ({ ...prev, harcirah_turu: e.target.value }))} className={inputClassName}>
                <option value="sabit">Sabit Harcırah</option>
                <option value="aylik">Aylık Harcırah</option>
                <option value="dinamik">Dinamik Harcırah</option>
              </select>
              <div><label className="block text-xs text-gray-500 mb-1">İkamet Enlem</label><input type="number" step="0.00000001" value={formData.residence_latitude} onChange={(e) => setFormData((prev) => ({ ...prev, residence_latitude: e.target.value }))} className={inputClassName} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">İkamet Boylam</label><input type="number" step="0.00000001" value={formData.residence_longitude} onChange={(e) => setFormData((prev) => ({ ...prev, residence_longitude: e.target.value }))} className={inputClassName} /></div>
              <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2"><input type="checkbox" checked={Boolean(formData.is_control_responsible)} onChange={(e) => setFormData((prev) => ({ ...prev, is_control_responsible: e.target.checked }))} className="h-4 w-4" /><span className="text-sm text-gray-700">Kontrol Sorumlusu</span></label>
            </div>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Evraklar</h3>
              <p className="mt-1 text-xs text-gray-500">Evrak yukleme zorunlu degil. Kayitla beraber yukleyebilir veya daha sonra duzenleyebilirsiniz.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {APPLICATION_DOCUMENT_TYPES.map((documentType) => {
                const existing = existingDocuments.filter((item) => String(item.document_type || item.documentType || '') === documentType);
                const pending = pendingDocuments[documentType];
                return (
                  <div key={documentType} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <FileText className="h-4 w-4 text-gray-500" />
                        {APPLICATION_DOCUMENT_LABELS[documentType]}
                      </div>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
                        <Upload className="h-3 w-3" />
                        {pending || existing.length > 0 ? 'Degistir' : 'Yukle'}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(event) => {
                            const file = event.target.files?.[0] || null;
                            setPendingDocuments((current) => ({ ...current, [documentType]: file }));
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>

                    {pending ? (
                      <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
                        <div className="font-medium">Kaydedilecek yeni dosya</div>
                        <div className="mt-1 break-all">{pending.name}</div>
                        <button type="button" onClick={() => setPendingDocuments((current) => ({ ...current, [documentType]: null }))} className="mt-2 text-blue-700 underline">Secimi kaldir</button>
                      </div>
                    ) : null}

                    {existing.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {existing.map((item) => (
                          <div key={item.id} className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                            <span className="truncate">{item.file_name || item.fileName || 'Evrak'}</span>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => window.open(resolveFileUrl(item.file_path || item.filePath), '_blank', 'noopener,noreferrer')} className="text-emerald-700 hover:text-emerald-900">Goruntule</button>
                              <button type="button" onClick={() => void handleDeleteDocument(item.id)} className="text-red-600 hover:text-red-800">Sil</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
          <button type="button" onClick={onClose} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">Iptal</button>
          <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Kaydediliyor...' : (isEditMode ? 'Guncelle' : 'Kaydet')}</button>
        </div>
      </div>
    </div>
  );
}
